import { useTranslation } from 'react-i18next';
import { Package, AlertTriangle, TrendingDown, TrendingUp, AlertCircle } from 'lucide-react';
import type { GoodsReceivedNote } from '../../../../api/queries/purchasing.queries';
import { StatusBadge } from '../shared';

interface GRNCardProps {
  grn: GoodsReceivedNote;
  onClick: () => void;
}

export default function GRNCard({ grn, onClick }: GRNCardProps) {
  const { t, i18n } = useTranslation();

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const poNumber = typeof grn.poId === 'object' && 'poNumber' in grn.poId
    ? grn.poId.poNumber
    : null;

  const supplierName = grn.supplierId?.name || 'Unknown Supplier';

  const discrepancy = grn.discrepancySummary;

  return (
    <div
      className={`rounded-xl border bg-card hover:border-primary/50 hover:shadow-sm transition-all cursor-pointer ${
        grn.hasDiscrepancies ? 'border-l-4 border-l-yellow-500' : ''
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground">{grn.grnNumber}</h3>
              <StatusBadge status={grn.status} />
              {grn.hasDiscrepancies && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs font-medium">
                  <AlertTriangle className="h-3 w-3" />
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {poNumber && <span>{poNumber} • </span>}
              {supplierName}
            </p>
          </div>
        </div>

        {/* Received Info */}
        <div className="mt-4 flex items-center gap-5 text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Package className="h-4 w-4" />
            {grn.items.length} {t('pos.items')}
          </span>
          <span className="text-muted-foreground">
            {t('purchasing.received')}: {formatDate(grn.createdAt)}
          </span>
        </div>

        {/* Discrepancy Summary */}
        {discrepancy && grn.hasDiscrepancies && (
          <div className="mt-4 grid grid-cols-3 gap-2">
            {discrepancy.shortageCount > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                <TrendingDown className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                <span className="text-xs font-medium text-orange-700 dark:text-orange-400">
                  {discrepancy.shortageCount} {t('purchasing.shortages')}
                </span>
              </div>
            )}
            {discrepancy.excessCount > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-xs font-medium text-blue-700 dark:text-blue-400">
                  {discrepancy.excessCount} {t('purchasing.excess')}
                </span>
              </div>
            )}
            {discrepancy.damagedCount > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-rose-100 dark:bg-rose-900/30">
                <AlertCircle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                <span className="text-xs font-medium text-rose-700 dark:text-rose-400">
                  {discrepancy.damagedCount} {t('purchasing.damaged')}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Pending Decisions */}
        {discrepancy && discrepancy.pendingDecisions > 0 && (
          <div className="mt-4 pt-4 border-t">
            <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
              {discrepancy.pendingDecisions} {t('purchasing.pendingDecisions')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
